import React from 'react';
import styled from 'styled-components/macro';
import { AutomotiveRole } from 'lib/const';
import { InventoryItem, useAuth } from 'lib/context';
import { VehicleSearch } from 'app/pages/library/videos/videoList/components/SendAndShare';
import { useFormikContext } from 'formik';
import { FormikDropdownField } from '../../../../../lib/components/formik/FormikDropdown';
import { UsersByAutmotiveRolesResponse } from 'lib/api/users/automotive/useUsersByAutomotiveRolesQuery';
import { UseQueryResult } from 'react-query';
import { DetailsPreviewRecordFormValues } from '../types';
import { FormikInputField } from 'lib/components/formik/FormikInputField';
import { FormikLabel } from 'lib/components/formik/FormikLabel';
import { FormikTextAreaField } from 'lib/components/formik/FormikTextAreaField';
import { useTranslation } from 'react-i18next';

const Wrapper = styled.div`
  margin-top: 24px;
`;

interface IVideoRequestDetailsProps {
  fetchSoldVehiclesForAutomotive?: boolean;
  results: UseQueryResult<UsersByAutmotiveRolesResponse[], unknown>[];
}

export const VideoRequestDetails = ({
  fetchSoldVehiclesForAutomotive = true,
  results,
}: IVideoRequestDetailsProps) => {
  const { userData } = useAuth();
  const { t } = useTranslation();
  const { setFieldValue, values } =
    useFormikContext<DetailsPreviewRecordFormValues>();
  const showVideoRequestFields = userData.isAutomotive;
  const isAutomotiveSales = userData.isAutomotiveSalesRole;
  const isAutomotiveService = userData.isAutomotiveServiceRole;
  const isTechnician = userData.automotiveRole === AutomotiveRole.TECHNICIAN;

  const advisorOptions = results.flatMap(result => {
    if (result.data) {
      return result.data.map(assignee => ({
        label: `${assignee.firstName} ${assignee.lastName}`,
        value: `${assignee.id}`,
      }));
    }
    return [];
  });

  if (!showVideoRequestFields) {
    return <></>;
  }

  return (
    <Wrapper>
      {isAutomotiveSales && (
        <>
          <div style={{ marginBottom: 16 }}>
            <FormikLabel label={t('Search vehicle')} isRequired={false} />
            <VehicleSearch
              onChangeVehicle={(item: InventoryItem) => {
                setFieldValue('videoRequest.vehicle', {
                  vin: item?.vin || '',
                  stockNumber: item?.stockNumber || '',
                });
              }}
              customSelectStyle={{
                menu: base => ({
                  ...base,
                  minWidth: 650,
                }),
              }}
              selectedVin={values.videoRequest?.vehicle?.vin || ''}
              placeholder={t('Search vehicle by name, VIN or stock number...')}
              fetchSold={fetchSoldVehiclesForAutomotive}
            />
          </div>

          <FormikInputField
            name={`videoRequest.vehicle.vin`}
            label={t('VIN')}
            isRequired={false}
            disabled={true}
          />
          <FormikInputField
            name={`videoRequest.vehicle.stockNumber`}
            label={t('Stock number')}
            isRequired={false}
            disabled={true}
          />
        </>
      )}
      {isAutomotiveService && (
        <FormikDropdownField
          name={`videoRequest.advisorId`}
          label={t('Assign To')}
          disabled={false}
          isRequired={isTechnician}
          options={advisorOptions}
        />
      )}
      {isAutomotiveService && (
        <FormikInputField
          name='videoRequest.repairOrderNumber'
          label={t('RO Number')}
          isRequired={isTechnician}
        />
      )}
      <FormikInputField
        name='videoRequest.customerName'
        label={t('Customer Name')}
        isRequired={false}
      />
      {isAutomotiveService && (
        <FormikTextAreaField
          name='videoRequest.note'
          label={t('Note')}
          isRequired={false}
        />
      )}
    </Wrapper>
  );
};
